import { GlobalOutlined } from "@ant-design/icons"
import { Form, type FormRule } from "components/Form/Form"
import { Input } from "components/Input/Input"
import { Select } from "components/Select/Select"
import { Switch } from "components/Switch/Switch"
import { AdminCareOrganization } from "interface/careOrganization"
import { SelectProvider } from "./SelectProvider"

export function CareOrganizationFormFields({
	careOrganizationId,
	fields = ["name", "supervisorId", "timezone", "excludedFromAnalytics"],
}: {
	careOrganizationId?: AdminCareOrganization["id"]
	fields?: Array<
		keyof Pick<
			AdminCareOrganization,
			"name" | "supervisorId" | "timezone" | "excludedFromAnalytics"
		>
	>
}) {
	return (
		<>
			{fields.includes("name") && (
				<Form.Item name="name" label="Name" rules={NAME_RULES}>
					<Input aria-label="Name" placeholder="Care organization name" />
				</Form.Item>
			)}
			{fields.includes("timezone") && (
				<Form.Item
					name="timezone"
					label="Timezone"
					rules={[{ required: true, message: "Please pick a timezone" }]}
				>
					<Select<string>
						aria-label="Timezone"
						showSearch={{ optionFilterProp: "label" }}
						options={TIMEZONE_OPTIONS}
						prefix={<GlobalOutlined aria-hidden />}
					/>
				</Form.Item>
			)}
			{fields.includes("supervisorId") && careOrganizationId && (
				<Form.Item name="supervisorId" label="Supervisor">
					<SelectProvider
						aria-label="Supervisor"
						careOrganizationId={careOrganizationId}
					/>
				</Form.Item>
			)}
			{fields.includes("excludedFromAnalytics") && (
				<Form.Item
					name="excludedFromAnalytics"
					label="Excluded from analytics"
					valuePropName="checked"
					extra="Leaves the organization's data out of analytics calculations."
				>
					<Switch aria-label="Excluded from analytics" />
				</Form.Item>
			)}
		</>
	)
}

const NAME_RULES: FormRule[] = [
	{ required: true, whitespace: true, message: "Please enter a name" },
	{
		min: 2,
		transform: (value?: string) => value?.trim(),
		message: "Name must be at least 2 characters",
	},
]

const TIMEZONE_OPTIONS = Intl.supportedValuesOf("timeZone").map((timezone) => ({
	value: timezone,
	label: timezone,
}))
